import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import sessionStorage from "./utils/sessionStore";
import { Form, Button, Alert, Spinner, Badge } from "react-bootstrap";
import { FaPlus, FaSearch, FaEdit, FaClipboardCheck } from "react-icons/fa";
import { ChevronRight } from "lucide-react";
import SearchableSelect from "./components/SearchableSelect";
import CollapsibleFilters from "./components/CollapsibleFilters";
import GridPagination from "./components/GridPagination";
import { authHeaders } from "./QuoteCreate";
import { reviewStatusMeta, statusColor } from "./utils/reviewAccess";
import { hasAction, isAdminOrDistrRole } from "./utils/auth";
import {
  customersDescriptor,
  warm as warmRefData,
} from "./utils/refDataCache";
import * as searchCache from "./utils/searchCache";
import { QUOTES_KEY, normalizeQuotesResponse } from "./utils/searchCache";
import {
  AUTOCOMPLETE_TERRITORY as AC_TERRITORY,
  buildSalesRepConfig,
  buildCustomerConfig,
} from "./utils/refDataConfigs";
import "./pagecss/Webappcss.css";
import "./pagecss/DynamicQw.css";
import "./pagecss/QuoteWizard.css";

const API_URL = process.env.REACT_APP_API_URL;

// -----------------------------------------------------------------------------
// Role-aware autocomplete configs — same scoping rules as QuoteCreate:
//   • Territory: backend filters by JWT scope; no extra params needed.
//   • Sales Rep: when a territory is picked, narrow to reps in that territory;
//     otherwise backend applies the user's territory subtree scope.
//   • Customer: narrows to the picked Sales Rep, else the picked Territory,
//     else (for non-D99-Admin/Distributor users) the user's own territory
//     subtree via an OData clause on SalesRepCode.
// -----------------------------------------------------------------------------
function getCurrentUserScope() {
  const role = (sessionStorage.getItem("userRole") || "").toLowerCase();
  const territoryId = sessionStorage.getItem("territoryId") || "";
  const isD99 = territoryId.toLowerCase() === "d99";
  return { role, territoryId, isUnrestricted: isAdminOrDistrRole(role) && isD99 };
}

// AC_TERRITORY / buildSalesRepConfig / buildCustomerConfig are imported from the
// shared utils/refDataConfigs module so this page populates identically to the
// create-quote page.

const PAGE_SIZES = [10, 25, 50, 100];

// -----------------------------------------------------------------------------
// Build the GET /api/Quote querystring for ONE page request. Pure — shared by
// runSearch and the background next-page prefetch so both produce identical
// requests (a prefetched page must be byte-for-byte the one Next would fetch).
// -----------------------------------------------------------------------------
// Exported: CustomerDetail's Activity tab reuses this for its quote download
// so both build byte-identical /api/Quote requests.
export function buildQuoteSearchQs({ filters, page, pageSize, cursor, quoteNumFind }) {
  const qs = new URLSearchParams();
  qs.set("page", String(page));
  qs.set("pageSize", String(pageSize));
  if (cursor?.dir === "next")      qs.set("beforeQuoteNum", String(cursor.quoteNum));
  else if (cursor?.dir === "prev") qs.set("afterQuoteNum", String(cursor.quoteNum));
  const qnum = quoteNumFind.trim();
  if (/^\d+$/.test(qnum)) qs.set("quoteNum", qnum);  // exact quote-number match
  if (filters.custID)        qs.set("custID", filters.custID);
  if (filters.salesRepCode)  qs.set("salesRepCode", filters.salesRepCode);
  if (filters.territoryID)   qs.set("territoryID", filters.territoryID);
  // Status → an EXCLUSIVE boolean combo so the filtered rows match the badge
  // that's actually shown (badge priority: Closed > Expired > Ordered > Quoted
  // > Open). E.g. "Ordered" = ordered AND not-expired AND not-closed, so it never
  // returns a quote that would render as a "Closed" badge.
  if (filters.status === "closed") {
    qs.set("quoteClosed", "true");
  } else if (filters.status === "expired") {
    qs.set("expired", "true"); qs.set("quoteClosed", "false");
  } else if (filters.status === "ordered") {
    qs.set("ordered", "true"); qs.set("expired", "false"); qs.set("quoteClosed", "false");
  } else if (filters.status === "quoted") {
    qs.set("quoted", "true"); qs.set("ordered", "false"); qs.set("expired", "false"); qs.set("quoteClosed", "false");
  } else if (filters.status === "open") {
    qs.set("quoted", "false"); qs.set("ordered", "false"); qs.set("expired", "false"); qs.set("quoteClosed", "false");
  }
  if (filters.entryDateFrom)      qs.set("entryDateFrom", filters.entryDateFrom);
  if (filters.entryDateTo)        qs.set("entryDateTo", filters.entryDateTo);
  if (filters.amountFrom !== "" && !isNaN(filters.amountFrom)) qs.set("amountFrom", String(filters.amountFrom));
  if (filters.amountTo !== "" && !isNaN(filters.amountTo))     qs.set("amountTo", String(filters.amountTo));
  qs.set("orderby", "QuoteNum desc");
  return qs.toString();
}

// Status filter — single-select, replaces the separate Quoted?/Closed? toggles.
// Each maps to one boolean param on Search (see runSearch).
const STATUS_OPTIONS = [
  { label: "Any status", value: "" },
  { label: "Open",       value: "open" },
  { label: "Quoted",     value: "quoted" },
  { label: "Ordered",    value: "ordered" },
  { label: "Expired",    value: "expired" },
  { label: "Closed",     value: "closed" },
];

// Light table header cell style — matches the territory/customer search grids.
// (Body cells get their padding/borders from the `.dyn-qw table td` CSS.)
const QTH = { padding: "9px 12px", fontWeight: 700, whiteSpace: "nowrap" };
// Per-column filter cell (the search row under the header labels).
const QTHF = { padding: "6px 8px", borderTop: "1px solid #E2E8F0", verticalAlign: "top", background: "#F8FAFC" };

function fmtMoney(v) {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(Number(v));
}
function fmtDate(v) {
  if (!v) return "—";
  try {
    const d = new Date(v);
    if (Number.isNaN(d.valueOf())) return "—";
    return d.toLocaleDateString();
  } catch { return "—"; }
}

// Compact M/D/YY — used only by the phone card (T-009), where the full
// "7/10/2026 → 10/7/2026" was eating the row and truncating the rep/territory.
// The desktop table keeps the full fmtDate.
function fmtDateShort(v) {
  if (!v) return "—";
  try {
    const d = new Date(v);
    if (Number.isNaN(d.valueOf())) return "—";
    return `${d.getMonth() + 1}/${d.getDate()}/${String(d.getFullYear()).slice(-2)}`;
  } catch { return "—"; }
}

// =============================================================================
// Quote — single landing page. Shows the search-and-results experience with a
// big "+ Add Quote" CTA and a row-click → /quote/edit/:n navigation. The
// dedicated /quote/new and /quote/edit/:n routes (already wired in index.js)
// are the deep-link aliases — this page is the one menu item.
// =============================================================================
function Quote() {
  const navigate = useNavigate();
  const location = useLocation();

  // Pre-applied filters handed in via router state — e.g. the Customer page's
  // Activity tab navigates here with { initialFilters: { custID: ... } }.
  // Captured once in a ref so re-renders don't re-read stale router state.
  const initialFiltersRef = useRef(location.state?.initialFilters || null);
  const initialFilters = initialFiltersRef.current;

  // Auth gate — same pattern as QuoteCreate / QuoteEdit.
  useEffect(() => {
    if (!sessionStorage.getItem("authToken")) navigate("/login");
  }, [navigate]);

  // ── User scope (JWT-derived, used to scope the customer autocomplete) ──
  const userScope = useMemo(() => getCurrentUserScope(), []);

  // ── Filter state ────────────────────────────────────────────────────────
  // Filters start BLANK for everyone (including sales reps): the panel opens
  // unfiltered and the auto-search on mount shows the most recent quotes. A
  // refresh therefore always starts clean. (A rep's own Territory + Sales Rep
  // used to be pre-filled here; that pre-fill was removed by request.)
  const [filters, setFilters] = useState(() => ({
    custID: "", salesRepCode: "", territoryID: "",
    status: "", expired: "",
    entryDateFrom: "", entryDateTo: "",
    amountFrom: "", amountTo: "",
    // Router-state pre-fill wins over the blank defaults (Activity tab flow).
    ...(initialFilters || {}),
  }));

  // Warm this rep's customer list in the background so the Customer dropdown
  // opens instantly once they expand it.
  useEffect(() => {
    if (filters.salesRepCode) warmRefData(customersDescriptor(filters.salesRepCode));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Configs rebuild when narrowing fields change so the dropdowns reflect
  // the active scope.
  const salesRepConfig = useMemo(
    () => buildSalesRepConfig(filters.territoryID),
    [filters.territoryID]
  );
  const customerConfig = useMemo(
    () => buildCustomerConfig(filters.salesRepCode, filters.territoryID, userScope),
    [filters.salesRepCode, filters.territoryID, userScope]
  );

  // Pagination state — separate from filters so changing pages doesn't lose
  // input and changing filters resets us back to page 1.
  const [page, setPage]         = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // ── Result state ────────────────────────────────────────────────────────
  // Seed from the login-time prefetch when it's there (the default search is
  // cached at sign-in and after every default-filter search), so the grid
  // paints rows on first render; the mount effect then revalidates quietly.
  // With pre-applied filters the cached (unfiltered) default page would flash
  // wrong rows — start empty and let the mount search paint the filtered set.
  const [results, setResults]   = useState(
    () => (initialFilters
      ? { quotes: [], totalCount: null, totalPages: 0 }
      : searchCache.peek(QUOTES_KEY) || { quotes: [], totalCount: null, totalPages: 0 })
  );
  // Cursor paging: { dir: "next"|"prev", quoteNum }. null = first page.
  // The list has no honest total — Epicor refuses $count past $skip=100, and
  // custID is filtered server-side in C# after the fetch — so we page by
  // handing back a boundary QuoteNum. MUST be cleared whenever the result set
  // changes (new search, cleared filters, page size), or we page from stale bounds.
  const [cursor, setCursor]     = useState(null);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState(null);
  // A cache-seeded grid already IS a search result — render count + pager
  // immediately instead of the "hit Search" empty state.
  const [hasSearched, setHasSearched] = useState(() => !initialFilters && searchCache.peek(QUOTES_KEY) !== undefined);
  // Quote # — exact-match server filter (sent as quoteNum on Search/Enter),
  // so it finds across all pages, not just the loaded rows.
  const [quoteNumFind, setQuoteNumFind] = useState("");
  // In-table (client-side) column search — narrows the ALREADY-LOADED page rows in
  // memory, like a data grid. It never calls the API (that's what the panel is for).
  const [colFilters, setColFilters] = useState({ quoteNum: "", customer: "", salesRep: "", territory: "", entry: "", expires: "", total: "", status: "" });
  const setCol = (k, v) => setColFilters((c) => ({ ...c, [k]: v }));

  // Expanded detail rows (quote → order → shipments flow). A Set so several
  // rows can be open at once; survives paging so flipping back keeps them open.
  const [expandedQuotes, setExpandedQuotes] = useState(() => new Set());
  const toggleExpanded = (quoteNum) => {
    setExpandedQuotes((prev) => {
      const next = new Set(prev);
      if (next.has(quoteNum)) next.delete(quoteNum); else next.add(quoteNum);
      return next;
    });
  };

  // Review status for the loaded rows — the local maker-checker workflow status
  // (PendingReview / Approved / OrderCreated …), which is NOT part of the Epicor
  // QuoteDTO. Fetched in ONE call per page (GET /api/QuoteReview/by-quotes) and
  // shown under the Epicor status. Map: quoteNum → { status, orderNum, … }.
  // Quotes never submitted for review simply aren't in the map (no badge).
  const [reviewByQuote, setReviewByQuote] = useState({});
  // Quote whose live conversion feed is open (null = closed). The grid's status
  // pill is the only way in, which is the point: the tracker is re-openable now.
  const [feedQuote, setFeedQuote] = useState(null);

  // Internal-scroll layout: filter panel collapses to a ribbon once the results
  // grid is scrolled, and a bottom fade signals more rows below.
  // Collapsed on first paint everywhere (phone and desktop alike) so the results
  // grid is what you land on; the ribbon expands on click.
  // Starts expanded when filters arrived via router state, so the user sees
  // which filters are pre-applied; collapsed otherwise.
  const [filtersCollapsed, setFiltersCollapsed] = useState(() => !initialFilters);
  const resultsScrollRef = useRef(null);
  const [moreBelow, setMoreBelow] = useState(false);
  const refreshScrollCue = () => {
    const el = resultsScrollRef.current;
    if (!el) { setMoreBelow(false); return; }
    setMoreBelow(el.scrollHeight - el.scrollTop - el.clientHeight > 8);
  };
  const onResultsScroll = (e) => {
    if (e.currentTarget.scrollTop > 24 && !filtersCollapsed) setFiltersCollapsed(true);
    refreshScrollCue();
  };
  useEffect(() => {
    const id = window.setTimeout(refreshScrollCue, 0);
    return () => window.clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [results, filtersCollapsed, loading]);

  // ───────────────────────────────────────────────────────────────────────────
  // runSearch — fires GET /api/Quote with the active filter set. The legacy
  // search page used DynamicSearch's auto-search-on-mount; we keep that
  // behavior so reps see recent quotes immediately.
  // ───────────────────────────────────────────────────────────────────────────
  // Per-result-set page cache: pageNum → normalized results, or a Promise for
  // a background prefetch still in flight. Visited pages rebind instantly on
  // Prev/Next, and the page AFTER the one on screen is always prefetching, so
  // a normal read-then-click-Next never waits on Epicor. Cleared whenever the
  // result set changes (Search click, Clear, page-size change) — NOT on
  // paging, which is the whole point.
  const pageCacheRef = useRef(new Map());
  const clearPageCache = () => { pageCacheRef.current = new Map(); };

  // Fire-and-forget fetch of one page into the page cache.
  const prefetchPage = useCallback((targetPage, cursorArg) => {
    const cache = pageCacheRef.current;
    if (cache.has(targetPage)) return; // loaded or already in flight
    const qs = buildQuoteSearchQs({ filters, page: targetPage, pageSize, cursor: cursorArg, quoteNumFind });
    const promise = fetch(`${API_URL}/api/Quote?${qs}`, { headers: authHeaders() })
      .then((r) => r.json().then((data) => {
        if (!r.ok || data.success === false) throw new Error("prefetch failed");
        const normalized = normalizeQuotesResponse(data);
        // Only commit if this cache generation is still current (a new search
        // may have swapped the Map while we were in flight).
        if (pageCacheRef.current === cache) cache.set(targetPage, normalized);
        return normalized;
      }))
      .catch(() => { cache.delete(targetPage); return null; });
    cache.set(targetPage, promise);
  }, [filters, pageSize, quoteNumFind]);

  // Warm the page after the one just shown, using its boundary cursor (live
  // mode) or the plain page number (known-total mode).
  const prefetchNext = useCallback((current, fromPage) => {
    if (current.hasMore !== undefined) {
      if (current.hasMore === true && current.lastQuoteNum != null)
        prefetchPage(fromPage + 1, { dir: "next", quoteNum: current.lastQuoteNum });
    } else if (fromPage + 1 <= (current.totalPages || 0)) {
      prefetchPage(fromPage + 1, null);
    }
  }, [prefetchPage]);

  // `silent` = background revalidate over a cache-seeded grid: no spinner, the
  // cached rows stay put until the fresh page lands.
  const runSearch = useCallback(async ({ silent = false } = {}) => {
    setError(null);

    // The mount query — blank filters, page 1 of 25, newest first — is the one
    // the session cache holds; every fresh copy of it is written back so a
    // return visit binds instantly.
    const isDefaultSearch =
      page === 1 && pageSize === 25 && !cursor && !quoteNumFind.trim() &&
      Object.values(filters).every((v) => !v);

    // Page cache first: a visited page binds instantly; an in-flight prefetch
    // is awaited (shows the spinner for its remaining time) instead of
    // duplicating the request.
    let entry = pageCacheRef.current.get(page);
    if (entry) {
      if (typeof entry.then === "function") {
        if (!silent) setLoading(true);
        entry = await entry; // normalized results, or null if the prefetch failed
      }
      if (entry) {
        setResults(entry);
        setLoading(false);
        setHasSearched(true);
        if (isDefaultSearch) searchCache.put(QUOTES_KEY, entry);
        prefetchNext(entry, page);
        return;
      }
    }

    if (!silent) setLoading(true);
    const qs = buildQuoteSearchQs({ filters, page, pageSize, cursor, quoteNumFind });

    try {
      const r = await fetch(`${API_URL}/api/Quote?${qs}`, { headers: authHeaders() });
      const data = await r.json().catch(() => ({}));
      if (!r.ok || data.success === false) {
        setError(data.error || data.message || `HTTP ${r.status}`);
        setResults({ quotes: [], totalCount: 0, totalPages: 0 });
      } else {
        const normalized = normalizeQuotesResponse(data);
        setResults(normalized);
        pageCacheRef.current.set(page, normalized);
        if (isDefaultSearch) searchCache.put(QUOTES_KEY, normalized);
        prefetchNext(normalized, page);
      }
    } catch (e) {
      setError(e.message);
      setResults({ quotes: [], totalCount: 0, totalPages: 0 });
    } finally {
      setLoading(false);
      setHasSearched(true);
    }
  }, [filters, page, pageSize, quoteNumFind, cursor, prefetchNext]);

  // Auto-load the first page on mount: top 25 within the logged-in user's
  // scope, newest quote first (orderby=QuoteNum desc). When the grid was
  // seeded from the session cache this runs as a silent revalidate — the
  // cached rows stay on screen and refresh in place.
  useEffect(() => {
    runSearch({ silent: !initialFilters && searchCache.peek(QUOTES_KEY) !== undefined });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-run on page/pageSize change once the first load has happened. Skips the
  // mount render — hasSearched can start true off the cache seed, and the
  // mount effect above already searches.
  const pagingMounted = useRef(false);
  useEffect(() => {
    if (!pagingMounted.current) { pagingMounted.current = true; return; }
    if (hasSearched) runSearch();
  }, [page, pageSize]);  // eslint-disable-line react-hooks/exhaustive-deps

  // After each page of quotes loads, fetch their review statuses in one batched
  // request and merge into reviewByQuote. Best-effort: any failure is swallowed
  // so the (supplementary) review badge never disrupts the quote list.
  useEffect(() => {
    const nums = (results.quotes || []).map((q) => q.quoteNum).filter((n) => n != null);
    if (nums.length === 0) { setReviewByQuote({}); return; }
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/QuoteReview/by-quotes?quoteNums=${nums.join(",")}`, { headers: authHeaders() });
        if (!r.ok) return;
        const data = await r.json().catch(() => []);
        if (cancelled || !Array.isArray(data)) return;
        const map = {};
        for (const row of data) if (row && row.quoteNum != null) map[row.quoteNum] = row;
        setReviewByQuote(map);
      } catch { /* ignore — review status is supplementary */ }
    })();
    return () => { cancelled = true; };
  }, [results.quotes]);

  // Reset to page 1 whenever the user explicitly searches.
  const handleSearchClick = () => {
    // Collapse the filter panel to its ribbon and reveal the results grid —
    // on every viewport, phone and desktop alike.
    setFiltersCollapsed(true);
    // A new search invalidates the cursor — its bounds belong to the old result
    // set — and the page cache with it (Search always means "fetch fresh").
    clearPageCache();
    setCursor(null);
    if (page !== 1) setPage(1); else runSearch();
  };

  // Paging. With cursor paging only the page above or below is reachable, so a
  // move is a cursor plus a direction; `page` survives as a display counter.
  const goToPage = (targetZeroBased) => {
    const target = targetZeroBased + 1;
    if (target === page) return;

    const liveMode = results.hasMore !== undefined;
    if (!liveMode) { setPage(target); return; }

    if (target > page && results.lastQuoteNum != null) {
      setCursor({ dir: "next", quoteNum: results.lastQuoteNum });
      setPage(page + 1);
    } else if (target < page && results.firstQuoteNum != null) {
      if (page <= 2) {
        // Back to the top — drop the cursor for the true first page rather
        // than "the N above wherever we happen to be".
        setCursor(null);
        setPage(1);
      } else {
        setCursor({ dir: "prev", quoteNum: results.firstQuoteNum });
        setPage(page - 1);
      }
    }
  };

  const handleClearFilters = () => {
    setFilters({ custID: "", salesRepCode: "", territoryID: "", status: "", expired: "", entryDateFrom: "", entryDateTo: "", amountFrom: "", amountTo: "" });
    setQuoteNumFind("");
    setColFilters({ quoteNum: "", customer: "", salesRep: "", territory: "", entry: "", expires: "", total: "", status: "" });
    clearPageCache();
    setCursor(null);
    setPage(1);
  };

  // Client-side per-column filtering of the loaded page (no API call). Each column's
  // text does a case-insensitive substring match against that column's displayed value.
  const cnorm = (s) => String(s ?? "").toLowerCase();
  const visibleQuotes = (() => {
    const f = colFilters;
    if (!Object.values(f).some((v) => v.trim() !== "")) return results.quotes;
    return results.quotes.filter((q) => {
      const st = quoteStatusText(q);
      const rv = reviewByQuote[q.quoteNum];
      const reviewLabel = rv ? reviewStatusMeta(rv.status).label : "";
      // Quote# filter also matches the resulting order number (shown under it).
      return (!f.quoteNum  || cnorm(q.quoteNum).includes(cnorm(f.quoteNum)) || cnorm(rv?.orderNum).includes(cnorm(f.quoteNum)))
          && (!f.customer  || cnorm(`${q.customerName} ${q.customerCustID}`).includes(cnorm(f.customer)))
          && (!f.salesRep  || cnorm(`${q.salesRepCode} ${q.salesRepName}`).includes(cnorm(f.salesRep)))
          && (!f.territory || cnorm(`${q.territoryID} ${q.territoryTerritoryDesc}`).includes(cnorm(f.territory)))
          && (!f.entry     || cnorm(fmtDate(q.entryDate)).includes(cnorm(f.entry)))
          && (!f.expires   || cnorm(fmtDate(q.expirationDate)).includes(cnorm(f.expires)))
          && (!f.total     || cnorm(fmtMoney(q.quoteAmt)).includes(cnorm(f.total)) || cnorm(q.quoteAmt).includes(cnorm(f.total)))
          // Status filter matches EITHER the Epicor status or the review status label,
          // so typing "review", "approved", "order created" narrows on the review badge too.
          && (!f.status    || cnorm(st).includes(cnorm(f.status)) || cnorm(reviewLabel).includes(cnorm(f.status)));
    });
  })();

  // ── Render ──────────────────────────────────────────────────────────────
  // Top padding = 78px so the page clears the fixed floating navbar.
  return (
    <div style={{ display: "flow-root" }}>
    <div className="qw-root content-halo dyn-qw dyn-search-shell">
      {/* Ribbon header — icon left, centered title, Add button right */}
      <div className="qw-header qw-header-centered" style={{ marginBottom: 16 }}>
        <div className="qw-header-icon">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
            <line x1="16" y1="13" x2="8" y2="13" />
            <line x1="16" y1="17" x2="8" y2="17" />
          </svg>
        </div>
        <div className="qw-header-title">Quotes</div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, justifySelf: "end" }}>
          {/* Entitlements: 00044 review queue, 00036 create quote (fallback
              true keeps pre-entitlement sessions working until re-login). */}
          {hasAction("00044", true) && (
          <button
            className="qw-btn"
            style={{ background: "rgba(255,255,255,.18)", color: "#fff", fontWeight: 600 }}
            onClick={() => navigate("/reviews")}
            title="Conversion review queue"
          >
            <FaClipboardCheck />
            <span className="d-none d-lg-inline ms-2">Reviews</span>
          </button>
          )}
          {hasAction("00036", true) && (
          <button
            className="qw-btn cust-add-btn"
            style={{ background: "#fff", color: "#4F46E5", fontWeight: 700 }}
            onClick={() => navigate("/quote/new")}
            title="Add quote"
            aria-label="Add quote"
          >
            <FaPlus className="cust-add-plus" />
            <span className="d-none d-lg-inline ms-2">Add Quote</span>
          </button>
          )}
        </div>
      </div>

      {/* ── Filters panel (mobile only) ──────────────────────────────────── */}
      {/* On desktop every filter lives in the grid column headers below; the
          collapsible panel renders only on phones, where the card list has no
          column headers. */}
      <div>
      <CollapsibleFilters collapsed={filtersCollapsed} onCollapsedChange={setFiltersCollapsed}>
      <div className="dyn-qw-panel" style={{ padding: "16px 20px", marginBottom: 16 }}>
        <div className="dyn-qw-filters dyn-qw-filters-q12">
          {/* Quote # — exact match (press Enter or Search). */}
          <div className="dyn-qw-filter-group qw-col-2">
            <label className="dyn-qw-filter-label">Quote #</label>
            <Form.Control
              size="sm"
              placeholder="Quote #…"
              value={quoteNumFind}
              onChange={(e) => setQuoteNumFind(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") handleSearchClick(); }}
            />
          </div>
          {/* Filter order: Customer → Sales Rep → Territory (most- to
              least-specific). The cascading narrowing/auto-fill between the
              three is unchanged — only the visual order moved. */}
          <div className="dyn-qw-filter-group qw-col-2">
            <label className="dyn-qw-filter-label">Customer</label>
            <SearchableSelect
              config={customerConfig}
              value={filters.custID}
              onChange={(v) => setFilters((f) => ({ ...f, custID: v }))}
              onSelectItem={(item) => {
                const id = item.custID || item.CustID || "";
                const repFromCust = item.salesRepCode || item.SalesRepCode || "";
                const terrFromCust = item.territoryID || item.TerritoryID || "";
                setFilters((f) => {
                  const next = { ...f, custID: id };
                  if (repFromCust && !f.salesRepCode) next.salesRepCode = repFromCust;
                  if (terrFromCust && !f.territoryID) next.territoryID = terrFromCust;
                  else if (!terrFromCust && repFromCust && !f.territoryID && repFromCust.includes("-")) {
                    next.territoryID = repFromCust.split("-")[0];
                  }
                  return next;
                });
              }}
              placeholder={
                filters.salesRepCode ? `Customers of ${filters.salesRepCode}…`
                  : filters.territoryID ? `Customers in ${filters.territoryID}…`
                  : "ID or name…"
              }
              inputWidth="100%"
            />
          </div>
          <div className="dyn-qw-filter-group qw-col-2">
            <label className="dyn-qw-filter-label">Sales Rep</label>
            <SearchableSelect
              inputWidth="100%"
              config={salesRepConfig}
              value={filters.salesRepCode}
              onChange={(v) => setFilters((f) => ({ ...f, salesRepCode: v }))}
              onSelectItem={(item) => {
                const code = item.salesRepCode || item.SalesRepCode || "";
                setFilters((f) => {
                  const next = { ...f, salesRepCode: code };
                  if (!f.territoryID && code.includes("-")) {
                    next.territoryID = code.split("-")[0];
                  }
                  if (f.custID) next.custID = "";
                  return next;
                });
              }}
              placeholder={filters.territoryID ? `Reps in ${filters.territoryID}…` : "Code or name…"}
            />
          </div>
          <div className="dyn-qw-filter-group qw-col-2">
            <label className="dyn-qw-filter-label">Territory</label>
            <SearchableSelect
              inputWidth="100%"
              config={AC_TERRITORY}
              value={filters.territoryID}
              onChange={(v) => setFilters((f) => ({ ...f, territoryID: v }))}
              onSelectItem={(item) => {
                const t = item.territoryID || item.TerritoryID || "";
                setFilters((f) => {
                  const next = { ...f, territoryID: t };
                  if (f.salesRepCode && !f.salesRepCode.startsWith(`${t}-`) && f.salesRepCode !== t) {
                    next.salesRepCode = "";
                  }
                  if (f.custID) next.custID = "";
                  return next;
                });
              }}
              placeholder="ID or description…"
            />
          </div>
          <div className="dyn-qw-filter-group qw-col-2">
            <label className="dyn-qw-filter-label">Status</label>
            <Form.Select size="sm" value={filters.status} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}>
              {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </Form.Select>
          </div>
          {/* Clear/Search actions. Layout is responsive (see .dyn-qw-actions-cell
              in DynamicQw.css): they sit inline at the end of the fields row on
              wide screens (≥1200px, where the row has room), and drop to their own
              full-width row at mid widths — a `qw-col-2` (1/6) cell was too narrow
              for both buttons at 768–1199px and they overflowed. flexWrap lets
              them stack rather than overflow on very narrow phones. */}
          <div className="dyn-qw-filter-group dyn-qw-actions-cell">
            <div className="d-flex gap-2" style={{ justifyContent: "flex-end", flexWrap: "wrap" }}>
              <Button type="button" variant="outline-secondary" size="sm" onClick={handleClearFilters} disabled={loading}>
                Clear
              </Button>
              <Button type="button" variant="primary" size="sm" onClick={handleSearchClick} disabled={loading} style={{ fontWeight: 600 }}>
                {loading
                  ? <><Spinner animation="border" size="sm" /> Searching…</>
                  : <><FaSearch style={{ marginRight: 6 }} /> Search</>}
              </Button>
            </div>
          </div>
        </div>
      </div>
      </CollapsibleFilters>
      </div>

      {/* ── Results panel — scrolls internally so the page stays put ───────── */}
      <div className="dyn-results-wrap">
      <div ref={resultsScrollRef} className="dyn-results-scroll" onScroll={onResultsScroll}>
      <div className="dyn-qw-panel" style={{ padding: "16px 20px" }}>
          {error && <Alert variant="danger">{error}</Alert>}

              {/* Desktop grid (≥992px) — header (filters + Search/Clear) stays
                  visible even with no results so you can always search. */}
              <div className="d-none d-lg-block" style={{ border: "1px solid #E2E8F0", borderRadius: 12, background: "#fff" }}>
              <table style={{ width: "100%", tableLayout: "fixed", borderCollapse: "collapse", fontSize: 13 }}>
                {/* Fixed proportional columns so the table fits the container exactly
                    (the results region clips horizontal overflow) and every filter
                    input lines up under its column. Widths in order:
                    Quote# 10, Customer 18, Sales Rep 14, Territory 13, Entry 13,
                    Expires 10, Total 10, Status 12. The Quote# cell also stacks the
                    resulting Order No link underneath. Keep each col alone on its line
                    with no trailing inline comment: an inline JSX comment after a
                    col leaves a whitespace text node, which is invalid inside a
                    colgroup and triggers a React hydration error. */}
                <colgroup>
                  <col style={{ width: "4%" }} />
                  <col style={{ width: "10%" }} />
                  <col style={{ width: "16%" }} />
                  <col style={{ width: "12%" }} />
                  <col style={{ width: "13%" }} />
                  <col style={{ width: "13%" }} />
                  <col style={{ width: "10%" }} />
                  <col style={{ width: "10%" }} />
                  <col style={{ width: "12%" }} />
                </colgroup>
                <thead>
                  <tr style={{ background: "#F8FAFC", textAlign: "left", color: "#64748B", fontSize: 11, textTransform: "uppercase", letterSpacing: ".04em" }}>
                    <th style={QTH} aria-label="Details" />
                    <th style={QTH}>Quote #</th>
                    <th style={QTH}>Customer</th>
                    <th style={QTH}>Sales Rep</th>
                    <th style={QTH}>Territory</th>
                    <th style={QTH}>Entry</th>
                    <th style={QTH}>Expires</th>
                    <th style={QTH}>Total</th>
                    <th style={QTH}>Status / Review</th>
                  </tr>
                  {/* Per-column CLIENT-SIDE search — instantly narrows the loaded rows
                      in memory (no API call). Each box substring-matches its column. */}
                  <tr>
                    <th style={QTHF} />
                    <th style={QTHF}><Form.Control size="sm" placeholder="Filter…" value={colFilters.quoteNum} onChange={(e) => setCol("quoteNum", e.target.value)} /></th>
                    <th style={QTHF}><Form.Control size="sm" placeholder="Filter…" value={colFilters.customer} onChange={(e) => setCol("customer", e.target.value)} /></th>
                    <th style={QTHF}><Form.Control size="sm" placeholder="Filter…" value={colFilters.salesRep} onChange={(e) => setCol("salesRep", e.target.value)} /></th>
                    <th style={QTHF}><Form.Control size="sm" placeholder="Filter…" value={colFilters.territory} onChange={(e) => setCol("territory", e.target.value)} /></th>
                    <th style={QTHF}><Form.Control size="sm" placeholder="Filter…" value={colFilters.entry} onChange={(e) => setCol("entry", e.target.value)} /></th>
                    <th style={QTHF}><Form.Control size="sm" placeholder="Filter…" value={colFilters.expires} onChange={(e) => setCol("expires", e.target.value)} /></th>
                    <th style={QTHF}><Form.Control size="sm" placeholder="Filter…" value={colFilters.total} onChange={(e) => setCol("total", e.target.value)} /></th>
                    <th style={QTHF}><Form.Control size="sm" placeholder="Filter…" value={colFilters.status} onChange={(e) => setCol("status", e.target.value)} /></th>
                  </tr>
                </thead>
                <tbody>
                  {visibleQuotes.map((q) => (
                    <React.Fragment key={q.quoteNum}>
                    <tr
                      style={{ cursor: "pointer" }}
                      onClick={() => navigate(`/quote/edit/${q.quoteNum}`)}
                      title="Click to edit"
                    >
                      {/* Details toggle — expands the quote → order → shipments
                          flow under the row. Only rendered when the quote has a
                          converted order (nothing to expand otherwise).
                          stopPropagation so it doesn't trigger click-to-edit. */}
                      <td onClick={(e) => e.stopPropagation()} style={{ cursor: "default" }}>
                        {(reviewByQuote[q.quoteNum]?.orderNums?.length || reviewByQuote[q.quoteNum]?.orderNum) ? (
                          <button
                            type="button"
                            className={"qflow-toggle" + (expandedQuotes.has(q.quoteNum) ? " open" : "")}
                            title={expandedQuotes.has(q.quoteNum) ? "Hide details" : "Show order & shipment details"}
                            aria-expanded={expandedQuotes.has(q.quoteNum)}
                            onClick={() => toggleExpanded(q.quoteNum)}
                          >
                            <ChevronRight size={16} />
                          </button>
                        ) : null}
                      </td>
                      {/* Order # lives in the expandable details flow now — no
                          duplicate link under the quote number. */}
                      <td className="fw-semibold" style={{ color: "#0052A5" }}>#{q.quoteNum}</td>
                      <td>
                        <div>{q.customerName || "—"}</div>
                        <div style={{ fontSize: 11, color: "#888" }}>{q.customerCustID || ""}</div>
                      </td>
                      <td>
                        <div>{q.salesRepCode || "—"}</div>
                        <div style={{ fontSize: 11, color: "#888" }}>{q.salesRepName || ""}</div>
                      </td>
                      <td>
                        <div>{q.territoryID || "—"}</div>
                        {q.territoryTerritoryDesc && <div style={{ fontSize: 11, color: "#888" }}>{q.territoryTerritoryDesc}</div>}
                      </td>
                      <td>{fmtDate(q.entryDate)}</td>
                      <td>{fmtDate(q.expirationDate)}</td>
                      <td>{fmtMoney(q.quoteAmt)}</td>
                      {/* Main Epicor status only — the review sub-status (e.g.
                          "Order Created") lives in the expandable flow, not
                          stacked under the badge. */}
                      <td>
                        <QuoteStatusBadge q={q} />
                        {/* In-flight review status only. The earlier decision to drop this line
                            was about "Order Created" duplicating the Epicor "Ordered" badge — see
                            the comment above and at the mobile card. That reasoning does not apply
                            while a quote is still converting: Epicor says "Open" throughout, so
                            without this line a quote that is In Review, Returned to Rep or FAILED
                            is indistinguishable from one nobody has touched. Terminal OrderCreated
                            stays hidden, exactly as designed — the badge and the flow chips say it. */}
                        {reviewByQuote[q.quoteNum]?.status
                          && reviewByQuote[q.quoteNum].status !== "OrderCreated" && (
                          <ReviewStatusLine
                            review={reviewByQuote[q.quoteNum]}
                            onOpen={() => setFeedQuote(q.quoteNum)}
                          />
                        )}
                      </td>
                    </tr>
                    {expandedQuotes.has(q.quoteNum) && (
                      <tr>
                        {/* Empty cell under the chevron column so the chips
                            start aligned under the Quote # column. */}
                        <td className="qflow-cell" />
                        <td colSpan={8} className="qflow-cell">
                          <QuoteFlow review={reviewByQuote[q.quoteNum]} navigate={navigate} />
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
              {loading && (
                <div style={{ padding: 24, textAlign: "center", color: "#666" }}>
                  <Spinner animation="border" size="sm" /> Loading quotes…
                </div>
              )}
              {!loading && results.quotes.length === 0 && (
                <div style={{ padding: 24, textAlign: "center", color: "#94A3B8" }}>
                  {hasSearched ? "No quotes match the current filters." : "Set filters and hit Search."}
                </div>
              )}
              {!loading && results.quotes.length > 0 && visibleQuotes.length === 0 && (
                <div style={{ padding: 24, textAlign: "center", color: "#94A3B8" }}>
                  No rows on this page match the column filters.
                </div>
              )}
              </div>

              {/* Mobile cards (<992px) — one card per quote, tap to edit */}
              <div className="d-lg-none">
                {!filtersCollapsed ? (
                  <Alert variant="light" className="text-center mb-0" style={{ fontSize: 13 }}>
                    Set filters above, then tap <strong>Search</strong> to see results.
                  </Alert>
                ) : (<>
                {loading && (
                  <div style={{ padding: 30, textAlign: "center", color: "#666" }}>
                    <Spinner animation="border" size="sm" /> Loading quotes…
                  </div>
                )}
                {!loading && !hasSearched && !error && (
                  <Alert variant="light">Set any filters above and hit <strong>Search</strong>, or hit <strong>+ Add Quote</strong> to create a new one.</Alert>
                )}
                {!loading && hasSearched && results.quotes.length === 0 && !error && (
                  <Alert variant="light">No quotes match the current filters.</Alert>
                )}
                {/* The rep scan is row-capped, so a high-volume rep silently
                    lost the older half of their quotes. Say so instead. */}
                {!loading && results.repScanTruncated && (
                  <Alert variant="warning" style={{ fontSize: 13 }}>
                    {results.message || "Showing only the most recent quotes for this rep — older ones aren't included. Narrow the filters to see them."}
                  </Alert>
                )}
                {!loading && results.quotes.map((q) => (
                  /* T-009: two dense rows so ~10 quotes fit a phone screen (was 4 rows / ~5).
                     Every text node in a row is a <span> in the SAME flex row, so they all
                     share one baseline — the old mix of <span> (quote #, total) and <div>
                     (customer, rep) rows was what made the text look mis-indented: the spans
                     picked up the global `span { margin-left: 16px }` from App.css and the
                     divs did not. .qw-quote-card cancels that indent. Layout is in
                     QuoteWizard.css. */
                  <div
                    key={q.quoteNum}
                    onClick={() => navigate(`/quote/edit/${q.quoteNum}`)}
                    className="qw-quote-card"
                  >
                    {/* Row 1: details toggle · quote # · customer (+ id) · status.
                        Toggle only when a converted order exists; a hidden
                        placeholder keeps the quote #s aligned across cards. */}
                    <div className="qc-row">
                      {(reviewByQuote[q.quoteNum]?.orderNums?.length || reviewByQuote[q.quoteNum]?.orderNum) ? (
                        <button
                          type="button"
                          className={"qflow-toggle qflow-toggle-sm" + (expandedQuotes.has(q.quoteNum) ? " open" : "")}
                          title={expandedQuotes.has(q.quoteNum) ? "Hide details" : "Show order & shipment details"}
                          aria-expanded={expandedQuotes.has(q.quoteNum)}
                          onClick={(e) => { e.stopPropagation(); toggleExpanded(q.quoteNum); }}
                        >
                          <ChevronRight size={13} />
                        </button>
                      ) : (
                        <span className="qflow-toggle qflow-toggle-sm" style={{ visibility: "hidden" }} aria-hidden="true" />
                      )}
                      <span className="qc-num">#{q.quoteNum}</span>
                      <span className="qc-cust" title={q.customerName || ""}>
                        {q.customerName || "—"}
                        {q.customerCustID ? <span className="qc-custid"> · {q.customerCustID}</span> : null}
                      </span>
                      {/* Just the Epicor badge — the review status moved to the last
                          row, which frees this whole column's width for the
                          customer name. */}
                      <div className="qc-status">
                        <QuoteStatusBadge q={q} />
                      </div>
                    </div>
                    {/* Row 2: total · rep + territory · dates · edit */}
                    <div className="qc-row">
                      <span className="qc-total">{fmtMoney(q.quoteAmt)}</span>
                      <span className="qc-meta" title={q.salesRepName || ""}>
                        {q.salesRepCode || "—"} · {q.territoryID || "—"}
                      </span>
                      <span className="qc-dates">
                        {fmtDateShort(q.entryDate)} → {fmtDateShort(q.expirationDate)}
                      </span>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="qc-edit"
                        onClick={(e) => { e.stopPropagation(); navigate(`/quote/edit/${q.quoteNum}`); }}
                        title="Edit quote"
                      >
                        <FaEdit />
                      </Button>
                    </div>
                    {/* Row 3 (last line): the conversion outcome. Once an order exists it
                        is the order link — the review sub-status "Order Created" stays gone
                        per design, since the Epicor badge in row 1 already says it. WHILE the
                        quote is still converting there is no order to link yet, so the same
                        slot carries the live status pill instead: one line, one meaning. A
                        quote that was never submitted still has no row 3 at all. Same rule as
                        the desktop column. */}
                    {reviewByQuote[q.quoteNum]?.orderNum ? (
                      <div className="qc-row qc-orderline">
                        <span className="qc-orderno">
                          Order <OrderNoLink review={reviewByQuote[q.quoteNum]} onOpen={(n) => navigate(`/order/${n}`)} />
                        </span>
                      </div>
                    ) : (reviewByQuote[q.quoteNum]?.status
                          && reviewByQuote[q.quoteNum].status !== "OrderCreated") ? (
                      <div className="qc-row qc-orderline">
                        <ReviewStatusLine
                          review={reviewByQuote[q.quoteNum]}
                          compact
                          onOpen={() => setFeedQuote(q.quoteNum)}
                        />
                      </div>
                    ) : null}
                    {expandedQuotes.has(q.quoteNum) && (
                      <div className="qflow-mobile" onClick={(e) => e.stopPropagation()}>
                        <QuoteFlow review={reviewByQuote[q.quoteNum]} navigate={navigate} />
                      </div>
                    )}
                  </div>
                ))}
                </>)}
              </div>

      </div>
      </div>
      {moreBelow && <div className="dyn-results-fade" aria-hidden="true" />}
      </div>
      {/* Pinned pagination footer (outside the scroll region) — shared design
          with Territory / Customers; rows-per-page rides along in the middle. */}
      {hasSearched && (
        <GridPagination
          currentPage={page - 1}
          totalPages={results.totalPages || 0}
          // Always the simple Prev/Next pager (hasNext defined ⇒ no numbered
          // pages). Cursor path: the server states outright whether more rows
          // exist. Known-total path (rep-scan): compare against the page
          // count. Legacy path with no total: a full page suggests another.
          hasNext={results.hasMore !== undefined
            ? results.hasMore === true
            : ((results.totalPages || 0) > 0
                ? page < results.totalPages
                : results.quotes.length >= pageSize)}
          totalCount={results.totalCount ?? null}
          showFrom={results.quotes.length ? (page - 1) * pageSize + 1 : 0}
          showTo={(page - 1) * pageSize + results.quotes.length}
          onGoToPage={goToPage}
          extra={
            <div className="d-flex align-items-center" style={{ gap: 6 }}>
              <span style={{ fontSize: 12, color: "#64748B" }}>Rows</span>
              <Form.Select size="sm" value={pageSize} onChange={(e) => { clearPageCache(); setCursor(null); setPage(1); setPageSize(Number(e.target.value)); }} style={{ width: "auto", minWidth: 64 }} aria-label="Page size">
                {PAGE_SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
              </Form.Select>
            </div>
          }
        />
      )}
    </div>

    {feedQuote != null && (
      <ReviewFeedModal quoteNum={feedQuote} onClose={() => setFeedQuote(null)} />
    )}
    </div>
  );
}

// =============================================================================
// QuoteStatusBadge — pick a colour from the QuoteDTO booleans (closed > expired
// > quoted > open). Mirrors the existing convention from QuoteEdit.
// =============================================================================
// Plain-text status (mirrors QuoteStatusBadge) — used by the in-table column search.
function quoteStatusText(q) {
  if (q.quoteClosed) return "Closed";
  if (q.expired)     return "Expired";
  if (q.ordered)     return "Ordered";
  if (q.quoted)      return "Quoted";
  return "Open";
}

function QuoteStatusBadge({ q }) {
  if (q.quoteClosed) return <Badge bg="secondary">Closed</Badge>;
  if (q.expired)     return <Badge bg="warning" text="dark">Expired</Badge>;
  if (q.ordered)     return <Badge bg="success">Ordered</Badge>;
  if (q.quoted)      return <Badge bg="info">Quoted</Badge>;
  return <Badge bg="primary">Open</Badge>;
}

// =============================================================================
// ReviewStatusLine — the local maker-checker review status shown under the
// Epicor status badge. Colored text (matching the review queue's convention via
// reviewStatusMeta/statusColor). Renders nothing when the quote was never
// submitted for review (no row in the by-quotes lookup).
// =============================================================================
// `compact` — variant used by the phone card's last row, where the status sits
// right-aligned opposite the order link: smaller, no top margin, never wraps or
// shrinks.
// =============================================================================
// ReviewFeedModal — the live conversion feed, re-openable from the quotes grid.
//
// This is the screen the rep previously saw ONCE, inside the convert wizard, and
// could never get back after closing it (ConvertToOrderModal resets to step 0 on
// every open). The status was never the problem — GET /api/QuoteReview/{n} is a
// durable server resource carrying stage, label, kind and the attributed step
// history. It simply had no second door. This is that door.
//
// Polls only while the review is still moving, and only while the tab is visible.
// =============================================================================
const FEED_NON_TERMINAL = ["PendingReview", "Approved", "Creating"];

function ReviewFeedModal({ quoteNum, onClose }) {
  const [r, setR] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!quoteNum) return;
    let cancelled = false;
    let timer = null;

    const load = async () => {
      try {
        const res = await fetch(`${API_URL}/api/QuoteReview/${encodeURIComponent(quoteNum)}`, { headers: authHeaders() });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (cancelled) return;
        setR(data); setErr(null);
        // Keep polling only while something can still change, and only when the
        // tab is in front — a background tab polling every 6s for hours is waste.
        if (FEED_NON_TERMINAL.includes(data?.status) && document.visibilityState === "visible")
          timer = setTimeout(load, 6000);
      } catch (e) {
        if (!cancelled) setErr(String(e.message || e));
      }
    };
    load();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [quoteNum]);

  const meta = r ? reviewStatusMeta(r.status) : null;
  const accent = meta ? statusColor(meta.kind) : "#64748B";
  const stage = Math.max(0, Math.min(5, Number(r?.stage) || 0));
  const live = r && FEED_NON_TERMINAL.includes(r.status);

  return (
    <div className="qrev-modal-backdrop" onClick={onClose}>
      <div className="qrev-modal" onClick={(e) => e.stopPropagation()}>
        <div className="qrev-modal-head">
          <div>
            <div className="qw-eyebrow" style={{ color: "#64748B" }}>Conversion status</div>
            <div style={{ fontSize: 17, fontWeight: 800, color: "#0F172A" }}>Quote #{quoteNum}</div>
          </div>
          <button type="button" className="qrev-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        {err && <Alert variant="danger" className="mb-0" style={{ margin: 16 }}>Couldn’t load status — {err}</Alert>}

        {!r && !err && (
          <div style={{ padding: 34, textAlign: "center" }}><Spinner animation="border" /></div>
        )}

        {r && (
          <div style={{ padding: "18px 20px 20px" }}>
            <div style={{ textAlign: "center", marginBottom: 18 }}>
              <div
                className={"qrev-medallion" + (live ? " is-live" : "")}
                style={{ background: accent, color: "#fff" }}
              >
                {r.status === "OrderCreated" ? "✓" : r.status === "CreationFailed" ? "!" : live ? "●" : "◔"}
              </div>
              <div className="qw-eyebrow" style={{ color: accent, marginTop: 10 }}>{r.statusLabel || r.status}</div>
              {r.customerName && (
                <div style={{ fontSize: 12.5, color: "#64748B", marginTop: 2 }}>
                  {r.customerName}{r.grandTotal ? ` · ${fmtMoney(r.grandTotal)}` : ""}
                </div>
              )}
            </div>

            {/* Same 5-stop rail as the wizard, so the two screens read identically. */}
            <div style={{ display: "flex", gap: 4, marginBottom: 18 }}>
              {[1, 2, 3, 4, 5].map((n) => (
                <div key={n} style={{ flex: 1, height: 5, borderRadius: 999, background: n <= stage ? accent : "#E2E8F0" }} />
              ))}
            </div>

            {r.status === "OrderCreated" && (r.orderNums?.length || r.orderNum) && (
              <div className="qrev-order">
                Order {(r.orderNums?.length ? r.orderNums : [r.orderNum]).join(", ")} created
              </div>
            )}
            {r.status === "CreationFailed" && r.creationError && (
              <Alert variant="danger" style={{ fontSize: 12.5 }}>{r.creationError}</Alert>
            )}
            {r.status === "ChangesRequested" && r.returnReason && (
              <Alert variant="warning" style={{ fontSize: 12.5 }}>{r.returnReason}</Alert>
            )}

            {Array.isArray(r.reviewerChain) && r.reviewerChain.length > 0 && (
              <>
                <div className="qw-eyebrow" style={{ marginBottom: 8, color: "#64748B" }}>History</div>
                <div className="qrev-feed">
                  {r.reviewerChain.map((s, i) => (
                    <div key={i} className="qrev-feed-row">
                      <span className="qrev-feed-dot" style={{ background: i === r.reviewerChain.length - 1 ? accent : "#CBD5E1" }} />
                      <span className="qrev-feed-label">{s.actionLabel || s.action}</span>
                      {s.actor && <span className="qrev-feed-actor">{s.actor}</span>}
                      <span className="qrev-feed-at">{s.at ? new Date(s.at).toLocaleString() : ""}</span>
                    </div>
                  ))}
                </div>
              </>
            )}

            {live && (
              <div style={{ fontSize: 11.5, color: "#94A3B8", marginTop: 12, textAlign: "center" }}>
                Updating automatically…
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/** #RRGGBB → rgba(), so one accent colour can drive text, a border tint and a fill tint. */
function tint(hex, alpha) {
  const h = (hex || "").replace("#", "");
  if (h.length !== 6) return hex;
  const n = parseInt(h, 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
}

/**
 * The ONLY statuses that get the breathing ring: a quote still moving through
 * review. Deliberately the same set the feed polls on (FEED_NON_TERMINAL), so the
 * animation and the polling can never disagree about what "in flight" means.
 *
 * Excluded on purpose:
 *   • no review row at all  — never submitted, so there is nothing to watch
 *   • OrderCreated          — done; the Epicor "Ordered" badge already says so
 *   • ChangesRequested / CreationFailed — stopped, waiting on a HUMAN. A pulse
 *     there would read as progress when nothing is progressing; they get a solid
 *     dot in amber/red instead, which is the "look at me" signal that fits.
 * A ring on every row would be ambient noise and would stop meaning anything.
 */
const LIVE_REVIEW_STATUSES = new Set(FEED_NON_TERMINAL);

function ReviewStatusLine({ review, onOpen, compact = false }) {
  if (!review || !review.status) return null;
  const meta = reviewStatusMeta(review.status);
  // Clicking the review status opens the review queue on the matching bucket
  // (tab), so the user can see this quote among its peers in that state.
  // stopPropagation so it doesn't also trigger the row's edit navigation.
  const accent = statusColor(meta.kind);
  const live = LIVE_REVIEW_STATUSES.has(review.status);

  return (
    <span
      className={"qrev-chip" + (live ? " is-live" : "")}
      onClick={onOpen ? (e) => { e.stopPropagation(); onOpen(review.status); } : undefined}
      style={{
        color: accent,
        background: tint(accent, 0.09),
        borderColor: tint(accent, 0.30),
        marginTop: compact ? 0 : 4,
        fontSize: compact ? 10 : 10.5,
        cursor: onOpen ? "pointer" : "default",
      }}
      title={onOpen ? `${meta.label} — click for the live conversion feed` : meta.label}
    >
      <span className="qrev-dot" />
      {meta.label}
    </span>
  );
}

// =============================================================================
// OrderNoLink — the resulting sales-order number for a converted quote, shown in
// its own "Order No" column. Once the quote has been converted, the review row
// carries the order number; render it as a link to /order/:orderNum (OrderView).
// stopPropagation so the click opens the order, not the row's edit navigation.
// Renders a dash when the quote hasn't been converted (no order yet).
// =============================================================================
function OrderNoLink({ review, onOpen }) {
  const orderNum = review?.orderNum;
  if (!orderNum) return <span style={{ color: "#CBD5E1" }}>—</span>;
  return (
    <a
      href={`/order/${orderNum}`}
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); onOpen && onOpen(orderNum); }}
      style={{ fontWeight: 600, color: "#0052A5" }}
      title="Open the sales order this quote became"
    >
      #{orderNum}
    </a>
  );
}

// =============================================================================
// QuoteFlow — the expanded "details" row: a left-to-right linkage flow chart
//   Quote → Order (the sales order the quote converted to, via the review
//   workflow) → Shipments (every pack cut against that order).
// Loads lazily on first expand: one /api/sales-orders?orderNum= lookup (served
// from the API's order cache) plus one /api/shipments?orderNum= lookup, in
// parallel. Results are kept in a module-level cache so re-expanding a row is
// instant; the fetch still re-fires each expand to quietly refresh the cache
// (shipment status changes as the warehouse works).
// =============================================================================
const flowCache = new Map(); // orderNum -> { order, shipments }

// Order/shipment status texts mirror the Orders / Shipping pages' badges so
// the flow chart never disagrees with the grids.
function orderStatusText(o) {
  if (!o) return "";
  if (o.orderHeld) return "Held";
  return o.openOrder ? "Open" : "Closed";
}
function OrderFlowBadge({ o }) {
  const text = orderStatusText(o);
  const bg = text === "Held" ? "warning" : text === "Open" ? "primary" : "secondary";
  return <Badge bg={bg} text={bg === "warning" ? "dark" : undefined}>{text}</Badge>;
}
function shipFlowStatusText(s) {
  if (s.voided) return "Voided";
  const st = (s.shipStatus || "").toLowerCase();
  if (st.includes("invoic")) return "Invoiced";
  if (st.includes("ship"))   return "Shipped";
  if (st.includes("stage"))  return "Staged";
  if (st.includes("close"))  return "Closed";
  if (st.includes("open") || !s.shipStatus) return "Open";
  return s.shipStatus;
}
function ShipFlowBadge({ s }) {
  const text = shipFlowStatusText(s);
  const bg =
    text === "Voided" ? "danger"
    : text === "Invoiced" || text === "Shipped" ? "success"
    : text === "Staged" ? "warning"
    : text === "Closed" ? "secondary"
    : "primary";
  return <Badge bg={bg} text={bg === "warning" ? "dark" : undefined}>{text}</Badge>;
}

// Horizontal connector (line + arrowhead) between flow stages.
function FlowArrow() {
  return (
    <div className="qflow-link" aria-hidden="true">
      <span className="qflow-line" />
      <span className="qflow-head" />
    </div>
  );
}

// QuoteFlow — one flow row PER ORDER created from the quote. Converting a
// quote more than once is no longer allowed (one-quote-one-order rule, July
// 2026), but quotes converted while the retired additionalOrder flag existed
// (e.g. 134277) still carry several orders in `review.orderNums`; each renders
// its own Order → Packs chain. Falls back to the single legacy `orderNum`.
function QuoteFlow({ review, navigate }) {
  const orderNums = (review?.orderNums && review.orderNums.length
    ? review.orderNums
    : (review?.orderNum ? [review.orderNum] : []));

  if (orderNums.length === 0) {
    return (
      <div className="qflow" onClick={(e) => e.stopPropagation()}>
        <span className="qflow-chip qflow-ghost">No order created from this quote yet</span>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {orderNums.map((n) => (
        <OrderFlowRow key={n} orderNum={n} navigate={navigate} />
      ))}
    </div>
  );
}

// One Order → Packs chain. Fetches the order header + its shipments lazily
// (module-level flowCache keeps re-expands instant).
function OrderFlowRow({ orderNum, navigate }) {
  // PROGRESSIVE render — the Order chip (link) paints immediately; the status
  // badge and the pack chips stream in as their own requests land. The old
  // all-or-nothing gate held the whole row on a spinner until BOTH the order
  // lookup and the shipments lookup finished, which right after an API restart
  // (cold order/shipment caches, live Epicor lookups) meant several seconds of
  // "Loading…" per order. The two fetches were already parallel — the fix is
  // to stop making the render wait for the slower one.
  const cached = flowCache.get(orderNum) || {};
  const [order, setOrder] = useState(cached.order ?? null);
  // shipments: undefined = still loading, "warming" = server cache not ready,
  // [] = definitively none, [...] = packs.
  const [shipments, setShipments] = useState(cached.shipments ?? undefined);

  useEffect(() => {
    let cancelled = false;
    const headers = authHeaders();
    const merge = (patch) => flowCache.set(orderNum, { ...(flowCache.get(orderNum) || {}), ...patch });

    fetch(`${API_URL}/api/sales-orders?orderNum=${orderNum}&page=1&pageSize=1`, { headers })
      .then((r) => r.json())
      .then((res) => {
        if (cancelled) return;
        const o = Array.isArray(res?.orders) && res.orders.length ? res.orders[0] : null;
        if (o) { merge({ order: o }); setOrder(o); }
      })
      .catch(() => { /* badge is decorative — chip already rendered */ });

    fetch(`${API_URL}/api/shipments?orderNum=${orderNum}&page=1&pageSize=100&sortBy=PackNum&sortDir=asc`, { headers })
      .then((r) => r.json())
      .then((res) => {
        if (cancelled) return;
        const list = Array.isArray(res?.shipments) ? res.shipments : [];
        // Empty while the server's shipment cache is still warming is NOT
        // "no shipments" — say so instead of lying, and don't cache it.
        if (list.length === 0 && res?.cacheInfo && res.cacheInfo.warm === false) {
          setShipments("warming");
          return;
        }
        merge({ shipments: list });
        setShipments(list);
      })
      .catch(() => { if (!cancelled) setShipments((s) => (s === undefined ? "warming" : s)); });

    return () => { cancelled = true; };
  }, [orderNum]);

  // Compact one-line chips. The row above already shows the quote, so the
  // panel starts at the ORDER and flows into its packs.
  return (
    <div className="qflow" onClick={(e) => e.stopPropagation()}>
      {/* Just identity + status per chip — dates/amounts live on the
          order/shipping pages the chips link to. */}
      <span
        className="qflow-chip qflow-chip-order"
        role="button"
        title="Open the sales order"
        onClick={() => navigate(`/order/${orderNum}`)}
      >
        <span className="qflow-chip-label">Order</span>
        <span className="qflow-chip-id">#{orderNum}</span>
        {order && <OrderFlowBadge o={order} />}
      </span>

      <FlowArrow />

      {shipments === undefined ? (
        <span className="qflow-chip qflow-ghost"><Spinner animation="border" size="sm" /> Loading shipments…</span>
      ) : shipments === "warming" ? (
        <span className="qflow-chip qflow-ghost">Shipments still loading on the server — reopen in a minute</span>
      ) : shipments.length === 0 ? (
        <span className="qflow-chip qflow-ghost">No shipments yet</span>
      ) : (
        shipments.map((s) => (
          <span
            key={s.packNum}
            className="qflow-chip qflow-chip-ship"
            role="button"
            title="Open shipment details"
            onClick={() => navigate(`/shipment/${s.packNum}`)}
          >
            <span className="qflow-chip-label">Pack</span>
            <span className="qflow-chip-id">#{s.packNum}</span>
            <ShipFlowBadge s={s} />
          </span>
        ))
      )}
    </div>
  );
}

export default Quote;
